/**
 * @fileoverview "What do I still need to add?" — asked from the model's own
 * config, and answered with the same rule the Run button applies.
 *
 * The optimizer had no answer to this question anywhere on the page. The empty
 * state explains four ways to add stops and never mentions that a scenario also
 * needs a vehicle, so the ordinary first session is: add twelve stops, press Run,
 * and get a refusal about a table nobody said was required. The refusal itself is
 * behind a tooltip on a disabled-looking button, and the other statement of the
 * same fact is a red status icon's tooltip on a collapsed accordion. Both are
 * hover-only, and both arrive after the work.
 *
 * Two things this module exists to get right.
 *
 * 1. THE ITEMS ARE NOT A FIXED PAIR. `minimumRows` is per model:
 *    vehicle-routing needs `locations`, `vehicles` and `vehicle_types`;
 *    pickup-delivery needs those three plus `trips`. It reads as two only
 *    because a blank scenario is seeded with one `vehicle_types` row
 *    (store/data/input_data.json), and Clear Table can take that away. A
 *    hardcoded pair would complete while pickup-delivery's Run still refused,
 *    so the items come from the config every time.
 *
 * 2. A TICK HAS TO MEAN WHAT RUN MEANS. Row count alone is not the test. The
 *    add form refuses a wholly empty submit but does not enforce required
 *    fields, so typing only a name into "Add Vehicle" creates a row — count 1 —
 *    while `selectDashboardIsValid` reports "Vehicles table is invalid" and Run
 *    refuses. A tick earned on count would contradict the refusal one screen
 *    later. So a requirement is met when it has enough rows AND the dashboard
 *    validator does not object to that table, and the objection shown is the
 *    validator's own sentence rather than a second wording of it.
 *
 * What this deliberately does NOT do: claim readiness. Something outside the
 * minimum-rows tables can still block a solve (a gap in `travel_costs`), and
 * that stays the Run button's tooltip to report. This answers what is still
 * missing and stops there.
 */
import { STATIC_MODEL_SETTINGS, STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';
import { getEffectiveTableSettings } from 'store/functions/tableSettingsOverrides';

const EMPTY_LIST = Object.freeze([]);

/**
 * The row-level rule, in one place because two readers apply it: the dashboard
 * validator (tableSelectors' `checkTableValid`) and this checklist. `Valid` is
 * tri-state — `true`, `false`, or the string `'warning'` — and a warning row is
 * usable, so truthiness is the rule rather than `=== true`.
 */
export const rowIsValid = (row) => Boolean(row?.Valid);

/**
 * "Vehicles table must have at least 1 row" — the sentence the Run tooltip has
 * always shown. Exported so the validator and anything describing the same
 * requirement cannot drift into two phrasings of it.
 */
export const minRowsMessage = (tableTitle, minRows) =>
  `${tableTitle} table must have at least ${minRows} ${minRows === 1 ? 'row' : 'rows'}`;

/**
 * The tables this model cannot run without, in the order the Data Manager shows
 * them — so the checklist reads top-to-bottom in the same order as the tables
 * below it.
 *
 * @param {string} activeModelType
 * @param {object} [modelSettings] - injectable for tests.
 * @returns {string[]}
 */
export function requiredTableNames(activeModelType, modelSettings = STATIC_MODEL_SETTINGS) {
  const config = modelSettings?.[activeModelType]?.input;
  if (!config) return EMPTY_LIST;

  const minimums = config.minimumRows || {};
  const order = Array.isArray(config.order) ? config.order : Object.keys(minimums);

  return order.filter((tableName) => {
    if (!((minimums[tableName] || 0) > 0)) return false;
    if (!config.enabled?.[tableName]) return false;
    const settings = STATIC_TABLE_SETTINGS[tableName];
    return Boolean(settings) && !settings.isResultTable;
  });
}

/**
 * The tables a row here has to point at before it can be completed, taken from
 * the schema rather than written down: a required column whose dropdown is
 * sourced from another table cannot be filled while that table is empty.
 *
 * This is why the halves are ordered and not independent. A vehicle's
 * `Starting Location` comes from `locations`, so on an empty scenario there is
 * no vehicle to add yet — and offering the action anyway opens a form whose
 * required dropdown has nothing in it.
 *
 * @returns {{tableName: string, columnHeader: string}[]}
 */
export function prerequisiteTables(tableName, tableSettings = STATIC_TABLE_SETTINGS) {
  const settings = tableSettings?.[tableName];
  if (!settings) return EMPTY_LIST;

  const required = settings.isRequired || {};
  const sources = settings.dropdownSource || {};
  const headers = settings.columnHeader || {};
  const seen = new Set();
  const out = [];

  for (const columnName of Object.keys(required)) {
    if (!required[columnName]) continue;
    const source = sources[columnName]?.tableName;
    if (!source || source === tableName || seen.has(source)) continue;
    seen.add(source);
    out.push({ tableName: source, columnHeader: headers[columnName] || columnName });
  }

  return out;
}

/** How many rows in this table the validator will reject. */
function invalidRowCount(rows) {
  return Array.isArray(rows) ? rows.filter((row) => !rowIsValid(row)).length : 0;
}

/**
 * The supporting line under an entry, or '' when the status says everything.
 *
 * For a table the validator objects to, this is the validator's message
 * unchanged. For one that cannot be started yet, it names what it is waiting for
 * and why — "Starting Location" is the column, so the sentence explains the
 * dependency instead of asserting an order the person has to take on trust.
 */
function detailFor({ state, waitingOn, message }) {
  if (state === 'attention') return message || '';
  if (state === 'todo' && waitingOn.length > 0) {
    const source = waitingOn[0];
    return `Add ${source.title} first — every row here has to name a ${source.columnHeader}.`;
  }
  return '';
}

/**
 * One entry per requirement, each resolving on its own.
 *
 * `state` is the whole point: 'done' when the requirement is met, 'todo' when
 * rows are still needed, 'attention' when the rows exist but the validator
 * objects. A glance down the states answers "am I done" without counting
 * anything.
 *
 * `invalidTables` / `invalidMessages` come from `selectDashboardIsValid` and are
 * index-aligned, so an 'attention' entry can carry the validator's own sentence
 * instead of inventing a second one.
 *
 * @param {object} args
 * @param {Record<string, object[]>} args.data - `inputData.present.data`.
 * @param {string} args.activeModelType
 * @param {object} [args.tableLabels] - the scenario's label pack.
 * @param {string} [args.coordinateSystem]
 * @param {string[]} [args.invalidTables]
 * @param {string[]} [args.invalidMessages]
 * @returns {ReadonlyArray<object>} frozen; one entry per required table.
 */
export function setupChecklist({
  data,
  activeModelType,
  tableLabels,
  coordinateSystem,
  invalidTables = EMPTY_LIST,
  invalidMessages = EMPTY_LIST,
}) {
  const tables = data && typeof data === 'object' ? data : {};
  const minimums = STATIC_MODEL_SETTINGS?.[activeModelType]?.input?.minimumRows || {};
  const titleOf = (tableName) =>
    getEffectiveTableSettings(tableName, coordinateSystem, tableLabels)?.title || tableName;

  const items = requiredTableNames(activeModelType).map((tableName) => {
    const rows = Array.isArray(tables[tableName]) ? tables[tableName] : EMPTY_LIST;
    const minRows = minimums[tableName] || 0;
    const count = rows.length;
    const objectionIndex = invalidTables.indexOf(tableName);
    const hasEnough = count >= minRows;

    let state = 'done';
    if (!hasEnough) state = 'todo';
    else if (objectionIndex >= 0) state = 'attention';

    // Only while rows are still needed: once a row exists, what is wrong with it
    // is the validator's story, not a missing prerequisite.
    const waitingOn = state === 'todo'
      ? prerequisiteTables(tableName)
          .filter((source) => !(Array.isArray(tables[source.tableName]) && tables[source.tableName].length > 0))
          .map((source) => ({ ...source, title: titleOf(source.tableName) }))
      : EMPTY_LIST;

    const settings = STATIC_TABLE_SETTINGS[tableName] || {};

    return Object.freeze({
      tableName,
      title: titleOf(tableName),
      minRows,
      count,
      invalidCount: state === 'attention' ? invalidRowCount(rows) : 0,
      state,
      waitingOn: Object.freeze(waitingOn),
      /* The second line, when there is one. On 'attention' it is the VALIDATOR'S
         OWN SENTENCE, verbatim, so the checklist and the Run tooltip cannot come
         to say different things about one row. On a blocked 'todo' it is what the
         entry is waiting for. It is a second line rather than part of the status
         because the status has to stay short enough to scan down the column. */
      detail: detailFor({ state, waitingOn, message: invalidMessages[objectionIndex] }),
      /* An add action is offered only where it can succeed: rows are genuinely
         missing, the table accepts a form entry, and nothing it depends on is
         empty. Never on 'attention' — another row does not repair a broken one.
         Never for `locations`: the front door directly below is the answer for
         stops, and a second button for it would be a fifth way in. */
      offerAdd: state === 'todo'
        && waitingOn.length === 0
        && Boolean(settings.allowAddRow)
        && tableName !== 'locations',
      addLabel: getEffectiveTableSettings(tableName, coordinateSystem, tableLabels)?.addRowTitle
        || `Add to ${titleOf(tableName)}`,
    });
  });

  return Object.freeze(items);
}

/** Whether anything is still outstanding — i.e. whether there is a list to show. */
export function checklistIsOutstanding(items) {
  return Array.isArray(items) && items.some((item) => item.state !== 'done');
}

/**
 * The state phrase for one entry. Written so the reader never has to compare two
 * numbers to learn whether this half is settled.
 */
export function checklistItemStatus(item) {
  if (!item) return '';
  if (item.state === 'done') {
    return item.count === 1 ? '1 added' : `${item.count} added`;
  }
  if (item.state === 'attention') {
    /* The validator only objects to a table it found a rejected row in, so a
       count of zero here should be unreachable. It gets its own phrasing anyway:
       the alternative is emitting "0 of 3 cannot be used yet", which is a
       well-formed sentence that says nothing true. */
    if (!item.invalidCount) return 'added, but not usable yet';
    return item.invalidCount === 1
      ? '1 added, but it cannot be used yet'
      : `${item.invalidCount} of ${item.count} cannot be used yet`;
  }
  if (item.count === 0) return 'none yet';
  return `${item.count} of ${item.minRows} added`;
}
